import { ReactNode, useMemo, useState } from 'react';
import { ListFilterCategoryOrSelectItem, ListFilterSelectProps } from './ListFilterSelect';
import { Box, styled } from '@mui/material';
import { ListFilterSelectItemType } from './ListFilterSelectItem';

export interface ListFilterCategory {
  key: string;
  label: string;
  icon?: ReactNode;
}

export interface ListFilterOption {
  value: string;
  label: string;
  searchHelper?: string;
  icon?: ReactNode;
  endIcon?: ReactNode;
  category?: ListFilterCategory;
  query?: string;
}

export interface BaseListFilterSelectProps extends Omit<ListFilterSelectProps, 'items'> {
  options: ListFilterOption[];
  maxHeight?: number;
  enableSearch?: boolean;
  title?: string;
  groupedByCategory?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
  onSearch?: (value: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  selectedFirst?: boolean;
}

interface PrepareOptionsProps {
  searchValue: string;
  enableSearch: boolean;
  type: ListFilterSelectItemType;
  options: ListFilterOption[];
  handler: (option: ListFilterOption) => void;
  selectedValues?: Set<string>;
  groupedByCategory?: boolean;
  selectedFirst?: boolean;
}

const UNCATEGORIZED = '__UNCATEGORIZED__';

export function usePrepareOptions({
  options,
  handler,
  enableSearch,
  searchValue,
  type,
  selectedValues,
  groupedByCategory = false,
  selectedFirst = true,
}: PrepareOptionsProps) {
  const [initSelectedValues] = useState(selectedFirst ? selectedValues : undefined);

  return useMemo(() => {
    if (selectedFirst && initSelectedValues) {
      const orderMap = new Map(Array.from(initSelectedValues?.values()).map((item, index) => [item, index]));
      options.sort((a, b) => {
        const aIndex = orderMap.get(a.value);
        const bIndex = orderMap.get(b.value);
        if (aIndex !== undefined && bIndex !== undefined) {
          return aIndex - bIndex;
        }
        if (aIndex !== undefined) {
          return -1;
        }
        if (bIndex !== undefined) {
          return 1;
        }
        return 0;
      });
    }

    const groups: Record<string, ListFilterCategory> = {};
    const groupedOptions: Record<string, ListFilterCategoryOrSelectItem[] | undefined> = {};

    const searchValueTrimmed = searchValue.trim().toLowerCase() ?? '';
    for (const option of options) {
      const searchable =
        `${option.label} ${option.value} ${option.searchHelper} ${option.category?.label}`.toLowerCase();
      if (enableSearch && !searchable.includes(searchValueTrimmed)) {
        continue;
      }

      const newOption: ListFilterCategoryOrSelectItem = {
        ...option,
        type,
        selected: selectedValues?.has(option.value),
        onClick: () => handler(option),
      };

      let groupKey = UNCATEGORIZED;
      if (option.category) {
        groupKey = option.category.key;
        groups[groupKey] = option.category;
      }

      const groupOptions = groupedOptions[groupKey] ?? [];
      groupOptions.push(newOption);
      groupedOptions[groupKey] = groupOptions;
    }

    if (!groupedByCategory) {
      return groupedOptions[UNCATEGORIZED] ?? [];
    }

    const result: ListFilterCategoryOrSelectItem[] = [];
    for (const group of Object.values(groups)) {
      const groupOptions = groupedOptions[group.key];
      if (groupOptions) {
        result.push({
          type: 'category',
          label: group.label,
          icon: group.icon,
        });
        result.push(...groupOptions);
      }
    }

    return result;
  }, [
    selectedFirst,
    initSelectedValues,
    searchValue,
    groupedByCategory,
    options,
    enableSearch,
    type,
    selectedValues,
    handler,
  ]);
}

export const ListFilterSelectWrapLine = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.border.hover}`,
}));
